import React, { useEffect, useMemo, useState } from 'react';
import { Languages } from 'lucide-react';
import Select from 'react-select';
import { useLocalization } from './index';

export const LanguageSelector = ({ variant = 'default' }) => {
  const { changeLanguage, fetchAvailableLanguages, availableLanguages } =
    useLocalization();

  useEffect(() => {
    fetchAvailableLanguages();
  }, [fetchAvailableLanguages]);

  const supportedLanguages = useMemo(
    () => availableLanguages || [],
    [availableLanguages],
  );

  const resolveInitialLanguage = (langs) => {
    const codes = langs.map((l) => l.code);

    const cookieLang = document.cookie
      .split('; ')
      .find((row) => row.startsWith('selected_language='))
      ?.split('=')[1];

    if (cookieLang && codes.includes(decodeURIComponent(cookieLang)))
      return decodeURIComponent(cookieLang);

    const browserLang = navigator.language.split('-')[0];
    if (codes.includes(browserLang)) return browserLang;

    return codes[0];
  };

  const [current, setCurrent] = useState(() =>
    resolveInitialLanguage(supportedLanguages),
  );

  useEffect(() => {
    setCurrent(resolveInitialLanguage(supportedLanguages));
  }, [supportedLanguages]);

  const options = useMemo(
    () =>
      supportedLanguages.map((l) => ({
        value: l.code,
        label: l.labelNative,
        language: l,
      })),
    [supportedLanguages],
  );

  const selectedOption = useMemo(
    () => options.find((o) => o.value === current),
    [current, options],
  );

  const handleLanguageChange = (option) => {
    const lang = option.value;
    setCurrent(lang);
    changeLanguage(lang);
  };

  const inverted = variant === 'inverted';
  const rgb = inverted ? '255, 255, 255' : '0, 0, 0';
  const textColor = inverted ? '#fff' : '#000';

  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      borderWidth: 0,
      boxShadow: 'none',
      ':hover': {
        ...base[':hover'],
        backgroundColor: 'transparent',
        borderColor: 'transparent',
      },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: textColor }),
    placeholder: (base) => ({
      ...base,
      color: inverted ? `rgba(${rgb}, 0.80)` : `rgba(${rgb}, 0.60)`,
    }),
    singleValue: (base) => ({ ...base, color: textColor }),
  };

  // like on the login page, we soften the selected item as the colorus clash
  const selectTheme = (theme) => {
    // for default the original theme was fine
    if (!inverted) return theme;

    return {
      ...theme,
      colors: {
        ...theme.colors,
        // Soften the selected/active highlight (base is #007AFF).
        primary: 'rgba(0, 122, 255, 0.7)',
      },
    };
  };

  if (supportedLanguages.length <= 1) return null;

  return (
    <div
      className={`group flex items-center flex-nowrap rounded pl-2 ${
        variant === 'inverted'
          ? 'hover:bg-white/10 focus-within:bg-white/10'
          : 'hover:bg-black/5 focus-within:bg-black/5'
      }`}
    >
      <Languages
        className={`${
          variant === 'inverted' ? 'text-white' : 'text-black'
        } w-5 h-5 stroke-[1.5]`}
      />
      <div className="w-26">
        <Select
          inputId="languageSelector"
          value={selectedOption}
          options={options}
          onChange={handleLanguageChange}
          isSearchable={false}
          isClearable={false}
          menuPlacement="auto"
          styles={selectStyles}
          theme={selectTheme}
          formatOptionLabel={(opt) => (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>{opt.label}</span>
            </div>
          )}
        />
      </div>
    </div>
  );
};
